import React from 'react';

/**
 * Render rich text as React elements.
 * Supports:
 *   HTML tags: <strong>, <b>, <em>, <i>, <s>, <strike>, <del>, <br>
 *   Inline color: <span style="color:#hex"> or <font color="#hex">
 *   Legacy markdown: **bold**, *italic*, ***both***
 */
export function renderFormattedText(text) {
  if (!text) return text;

  // Detect HTML tags (our rich editor output)
  const hasHtml = /<(\w+)[\s>]/i.test(text);

  if (hasHtml) {
    return renderHtmlText(text);
  }

  // Legacy markdown
  const hasMarkdown = /\*{1,3}[^*]+\*{1,3}/.test(text);
  if (hasMarkdown) {
    return renderMarkdownText(text);
  }

  return text;
}

/**
 * Parse HTML into React elements.
 * Allowed: strong, b, em, i, s, strike, del, br, span[style=color], font[color]
 */
function renderHtmlText(html) {
  const parts = [];
  let key = 0;

  // Match tags with optional attributes
  const tagPattern = /<(\/?)(\w+)((?:\s+[^>]*)?)>/g;
  let lastIndex = 0;
  const stack = [];
  let current = parts;

  let match;
  while ((match = tagPattern.exec(html)) !== null) {
    const [fullMatch, isClosing, rawTag, attrs] = match;
    const tag = normalizeTag(rawTag.toLowerCase());

    // Text before this tag
    if (match.index > lastIndex) {
      const text = html.slice(lastIndex, match.index);
      if (text) current.push(decodeEntities(text));
    }
    lastIndex = match.index + fullMatch.length;

    if (tag === 'br') {
      current.push('\n');
      continue;
    }

    const isAllowed = tag === 'strong' || tag === 'em' || tag === 's' || tag === 'span';

    if (!isAllowed) continue;

    if (isClosing) {
      // Close — find matching frame in stack
      for (let i = stack.length - 1; i >= 0; i--) {
        if (stack[i].tag === tag) {
          const frame = stack.splice(i, 1)[0];
          const parent = stack.length > 0 ? stack[stack.length - 1].children : parts;

          if (tag === 'span' && frame.color) {
            parent.push(React.createElement('span', { key: key++, style: { color: frame.color } }, ...frame.children));
          } else {
            parent.push(React.createElement(tag, { key: key++ }, ...frame.children));
          }
          current = stack.length > 0 ? stack[stack.length - 1].children : parts;
          break;
        }
      }
    } else {
      // Open — push frame
      const color = extractColor(rawTag.toLowerCase(), attrs);
      const children = [];
      stack.push({ tag, children, color });
      current = children;
    }
  }

  // Remaining text
  if (lastIndex < html.length) {
    const text = html.slice(lastIndex);
    if (text) current.push(decodeEntities(text));
  }

  // Flush unclosed tags
  while (stack.length > 0) {
    const frame = stack.pop();
    const parent = stack.length > 0 ? stack[stack.length - 1].children : parts;
    if (frame.tag === 'span' && frame.color) {
      parent.push(React.createElement('span', { key: key++, style: { color: frame.color } }, ...frame.children));
    } else {
      parent.push(React.createElement(frame.tag, { key: key++ }, ...frame.children));
    }
  }

  if (parts.length === 0) return '';
  if (parts.length === 1 && typeof parts[0] === 'string') return parts[0];
  return parts;
}

/** Extract color from tag attributes (span style="color:..." or font color="...") */
function extractColor(tag, attrs) {
  if (!attrs) return null;
  // span style="color: #xxx" or style="color: rgb(...)"
  const styleMatch = attrs.match(/style\s*=\s*["'][^"]*color\s*:\s*([^;"']+)/i);
  if (styleMatch) return styleMatch[1].trim();
  // font color="#xxx"
  const colorMatch = attrs.match(/color\s*=\s*["']([^"']+)["']/i);
  if (colorMatch) return colorMatch[1].trim();
  return null;
}

/** Normalize tag names: b→strong, i→em, strike/del→s, font→span */
function normalizeTag(tag) {
  if (tag === 'b') return 'strong';
  if (tag === 'i') return 'em';
  if (tag === 'strike' || tag === 'del') return 's';
  if (tag === 'font') return 'span';
  return tag;
}

function decodeEntities(text) {
  return text
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ');
}

/** Legacy markdown → React elements */
function renderMarkdownText(text) {
  const pattern = /(\*\*\*(.+?)\*\*\*|\*\*(.+?)\*\*|\*(.+?)\*)/g;
  const parts = [];
  let lastIndex = 0;
  let key = 0;
  let m;

  while ((m = pattern.exec(text)) !== null) {
    if (m.index > lastIndex) parts.push(text.slice(lastIndex, m.index));
    if (m[2]) parts.push(<strong key={key}><em>{m[2]}</em></strong>);
    else if (m[3]) parts.push(<strong key={key}>{m[3]}</strong>);
    else if (m[4]) parts.push(<em key={key}>{m[4]}</em>);
    key++;
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  if (parts.length === 0) return text;
  if (parts.length === 1 && typeof parts[0] === 'string') return text;
  return parts;
}

/** Legacy markdown → HTML (for rich editor migration) */
export function markdownToHtml(text) {
  if (!text) return '';
  let html = text;
  html = html.replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>');
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
  return html;
}
